import { Button, Grid } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import type { AxiosError } from 'axios';
import axios from 'axios';
import * as React from 'react';
import useSWR from 'swr';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { ArrowForward, Inbox } from '@material-ui/icons';
import {
  useParams, useNavigate,
} from 'react-router-dom';
import GenericCard from '../global/GenericCard';
import RepairsScanned from '../massReceive/RepairsScanned';
import CourierOverrideModal from './CourierOverrideModal';
import CourierRescanModal from './CourierRescanModal';
import useEventListener from '../../lib/useEventListener';

const useStyles = makeStyles((theme) => createStyles({
  completeDropOffButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

interface ICourierRepair {
  id: string;
  barcode: string;
}

interface IBin {
  id: string;
  name: string;
  barcode: string;
}
interface ICourierBinScan {
  destinationBin: IBin;
  sourceBin: IBin;
  repairs: ICourierRepair[];
}

const CourierDropoff: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { barcode: barcodeUrlParam } = useParams();

  const [rescanModalOpen, setRescanModalOpen] = React.useState<boolean>(false);
  const [isRescan, setIsRescan] = React.useState<boolean>(false);
  const [managerModalOpen, setManagerModalOpen] = React.useState<boolean>(false);
  const [allRepairsReconciled, setAllRepairsReconciled] = React.useState<boolean>(false);
  const [destinationBinScanned, setDestinationBinScanned] = React.useState<boolean>(false);

  const [managerCode, setManagerCode] = React.useState<string>('');
  const [barcode, setBarcode] = React.useState<string | undefined>(barcodeUrlParam);
  const [unscannedRepairs, setUnscannedRepairs] = React.useState<ICourierRepair[]>([]);
  const [scannedRepairs, setScannedRepairs] = React.useState<ICourierRepair[]>([]);

  const buttonText = (unscannedItemLength: number, rescan: boolean) => {
    if (rescan) {
      return 'COMPLETE RESCAN';
    }
    if (unscannedItemLength > 0) {
      return 'MARK AS DROPPED OFF';
    }
    return 'MARK AS RECONCILED';
  };

  const repairFetcher = (url: string, barcodeParam: string) => axios.get<ICourierBinScan>(url, {
    params: {
      barcode: barcodeParam,
    },
  })
    .then((response) => {
      setUnscannedRepairs([...response.data.repairs]);
      setScannedRepairs([]);
      return response.data;
    })
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      navigate('/courier');
    });

  const { data } = useSWR(
    barcode
      ? ['/courier/getCourierBin', barcode]
      : null,
    (url, barcodeParam) => repairFetcher(url, barcodeParam), {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  const scanBin = (inputBarcode: string) => {
    if (data && data?.destinationBin?.barcode && inputBarcode !== '') {
      if (data?.destinationBin?.barcode === inputBarcode) {
        setDestinationBinScanned(true);
      } else {
        enqueueSnackbar(`Scanned barcode ${inputBarcode} does not match destination bin.`, { persist: false, variant: 'error' });
      }
    }
  };

  const dropOffRepairs = () => {
    axios.post('/courier/dropoffrepairs', {
      scannedRepairs,
      unscannedRepairs,
    })
      .then(() => {
        enqueueSnackbar(`Repairs have been successfully assigned to ${data?.destinationBin?.name || ''} bin.`, { variant: 'success' });
        navigate('/courier');
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const scanRepair = (inputBarcode: string) => {
    if (inputBarcode !== '') {
      const repairIndex = unscannedRepairs.findIndex(
        (repair) => repair.barcode === inputBarcode,
      );
      if (repairIndex === -1) {
        enqueueSnackbar(`Scanned barcode ${inputBarcode} does not exist in current repair bin.`, { persist: false, variant: 'error' });
      } else {
        const scannedRepair = unscannedRepairs[repairIndex];
        const tempStateUnscanned = unscannedRepairs;
        tempStateUnscanned.splice(repairIndex, 1);
        setUnscannedRepairs([...tempStateUnscanned]);
        const tempStateScanned = scannedRepairs;
        tempStateScanned.push(scannedRepair);
        setScannedRepairs([...tempStateScanned]);
      }
    }
  };

  const restartScan = () => {
    setUnscannedRepairs([...unscannedRepairs, ...scannedRepairs]);
    setScannedRepairs([]);
    setIsRescan(true);
    setRescanModalOpen(false);
  };

  const allRepairsScanned = Boolean(data && (allRepairsReconciled || unscannedRepairs.length === 0));

  const handleManagerOverride = () => {
    setManagerModalOpen(false);
    setAllRepairsReconciled(true);
  };

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key.length === 1 || e.key === 'Enter') {
      if (e.key !== 'Enter') {
        barcodeBuffer = `${barcodeBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented) {
          if (allRepairsScanned) {
            scanBin(barcodeBuffer);
          } else {
            scanRepair(barcodeBuffer);
          }
        }
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  return (
    <Grid container spacing={0}>
      <Grid item xs={1} />
      <Grid item xs={8} container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <Alert severity="info">
            {'Rescan in repairs from current bin, and place in the Temporary bin until all repairs are scanned in.'}
          </Alert>
        </Grid>
        { data
          ? (
            <>
              <CourierRescanModal
                open={rescanModalOpen}
                bin={data.sourceBin.name}
                onClose={restartScan}
              />
              <CourierOverrideModal
                open={managerModalOpen}
                managerCode={managerCode}
                setManagerCode={setManagerCode}
                handleManagerOverride={handleManagerOverride}
                onClose={() => setManagerModalOpen(false)}
              />
              <Grid item xs={6}>
                <GenericCard
                  primaryText="Current Bin"
                  secondaryText={allRepairsReconciled ? 'Temporary' : data.sourceBin.name}
                >
                  <Inbox />
                </GenericCard>
              </Grid>
              <Grid item xs={6}>
                <GenericCard
                  primaryText={allRepairsReconciled ? 'Scan bin then place items in bin' : 'Scan and place in Bin'}
                  secondaryText={allRepairsReconciled ? data.destinationBin?.name : 'Temporary'}
                >
                  <ArrowForward />
                </GenericCard>
              </Grid>
              {
              !allRepairsReconciled
              && (
                <Grid item xs={12}>
                  <RepairsScanned
                    unscannedRepairs={unscannedRepairs}
                    scannedRepairs={scannedRepairs}
                    loading={false}
                    scanRepair={() => { }}
                    unscannedColumnText={`Repairs in ${data.sourceBin.name}`}
                  />
                </Grid>
              )
              }
              <Grid item container direction="row-reverse">
                <Grid item>
                  {!allRepairsReconciled
                    ? (
                      <Button
                        fullWidth
                        id={`${buttonText(unscannedRepairs.length, isRescan)}`}
                        variant="contained"
                        color="primary"
                        className={isRescan ? undefined : classes.completeDropOffButton}
                        onClick={() => {
                          if (unscannedRepairs.length === 0) {
                            setAllRepairsReconciled(true);
                          } else if (isRescan) {
                            setManagerModalOpen(true);
                          } else {
                            setRescanModalOpen(true);
                          }
                        }}
                      >
                        {buttonText(unscannedRepairs.length, isRescan)}
                      </Button>
                    )
                    : (
                      <Button
                        id="complete-dropoff-button"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        className={classes.completeDropOffButton}
                        disabled={!destinationBinScanned}
                        onClick={() => { dropOffRepairs(); }}
                      >
                        {'Complete Dropoff'}
                      </Button>
                    )}
                </Grid>
              </Grid>
            </>
          )
          : (
            <>
              <Grid item xs={6}>
                <Skeleton width="100%">
                  <GenericCard
                    primaryText="Current Bin"
                    secondaryText="Temporary"
                  >
                    <Inbox />
                  </GenericCard>
                </Skeleton>
              </Grid>
              <Grid item xs={6}>
                <Skeleton width="100%">
                  <GenericCard
                    primaryText="Scan and place in Bin"
                    secondaryText="Temporary"
                  >
                    <Inbox />
                  </GenericCard>
                </Skeleton>
              </Grid>
              <Grid item xs={12}>
                <Skeleton width="100%">
                  <RepairsScanned
                    unscannedRepairs={unscannedRepairs}
                    scannedRepairs={scannedRepairs}
                    loading={false}
                    scanRepair={() => { }}
                    unscannedColumnText="Repairs in "
                  />
                </Skeleton>
              </Grid>
              <Grid item container direction="row-reverse">
                <Grid item>
                  <Button
                    disabled={!data}
                    fullWidth
                    variant="contained"
                    color="secondary"
                    className={classes.completeDropOffButton}
                    onClick={() => setBarcode(undefined)}
                  >
                    {'Mark as dropped off'}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
      </Grid>
      <Grid item xs={3} />
    </Grid>

  );
};

export default CourierDropoff;
